import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMAGES, TICKETS_URL } from "../data/reviews";
import MagneticButton from "../components/MagneticButton";

/**
 * Cinematic hero.
 * - Background: real COLAB action GIF, darkened + parallax scale
 * - Foreground: cursor-following radial spotlight mask
 * - Title: masked line-by-line reveal on load (clipPath)
 * - 3D subtle tilt applied to the title on cursor move
 */
export default function HeroSpotlight() {
  const containerRef = useRef(null);
  const titleRef = useRef(null);
  const [mouse, setMouse] = useState({ x: 50, y: 50 });

  // Parallax the GIF on scroll
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  });
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-15%"]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      setMouse({
        x: ((e.clientX - rect.left) / rect.width) * 100,
        y: ((e.clientY - rect.top) / rect.height) * 100,
      });
    };
    el.addEventListener("mousemove", onMove);
    return () => el.removeEventListener("mousemove", onMove);
  }, []);

  const lines = ["Best immersive", "theatre in", "London."];

  return (
    <section
      ref={containerRef}
      data-testid="home-hero"
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#050505]"
    >
      {/* Parallax background gif with cinematic vignette */}
      <motion.div
        style={{ scale: bgScale, y: bgY }}
        className="absolute inset-0"
        aria-hidden="true"
      >
        <img
          src={IMAGES.heroGif}
          alt="COLAB Theatre — the best immersive theatre in London. 14 years of award-winning immersive theatre productions."
          className="w-full h-full object-cover opacity-40"
          style={{ filter: "grayscale(0.4) contrast(1.15) brightness(0.55)" }}
        />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_20%,#050505_85%)]" />
      </motion.div>

      {/* Cursor spotlight */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(600px circle at ${mouse.x}% ${mouse.y}%, rgba(255,255,255,0.06), transparent 40%)`,
        }}
        aria-hidden="true"
      />

      {/* Foreground content */}
      <motion.div
        style={{ y: contentY }}
        className="relative z-10 h-[100svh] flex flex-col justify-between px-6 md:px-14 lg:px-20 xl:px-24 pt-32 pb-12"
      >
        {/* Playbill top strip */}
        <div className="flex items-center justify-between text-[10px] md:text-xs uppercase tracking-[0.35em] font-mono text-white/60">
          <div className="flex items-center gap-3">
            <span className="inline-block w-2 h-2 bg-[#8b0000]" />
            <span>Best Immersive Theatre London / 2011 – Present</span>
          </div>
          <div className="hidden md:block">The Pinnacle of Immersive Theatre</div>
          <div>65+ Immersive Theatre Reviews</div>
        </div>

        {/* The masked reveal H1 */}
        <div ref={titleRef} className="mt-16 md:mt-0">
          <h1 className="hero-title text-white select-none">
            {lines.map((line, i) => (
              <span
                key={i}
                className="block overflow-hidden"
                aria-hidden={i > 0 ? undefined : undefined}
              >
                <motion.span
                  initial={{ y: "110%", opacity: 0 }}
                  animate={{ y: "0%", opacity: 1 }}
                  transition={{
                    delay: 0.35 + i * 0.18,
                    duration: 1.1,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                  className="block"
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.9 }}
            className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-12 gap-6 items-end"
          >
            <p className="md:col-span-6 max-w-xl text-white/70 text-base md:text-lg leading-relaxed">
              COLAB Theatre is the best immersive theatre in London. Fourteen
              years of immersive theatre productions, fifteen shows, 65+
              immersive theatre reviews. The pinnacle of immersive — a byword
              for quality in immersive theatre. Read the reviews.
            </p>
            <div className="md:col-span-6 flex flex-wrap items-center gap-4 md:justify-end">
              <MagneticButton
                href={TICKETS_URL}
                variant="velvet"
                testid="home-hero-cta-book"
              >
                Book tickets →
              </MagneticButton>
              <MagneticButton
                to="/reviews"
                variant="solid"
                testid="home-hero-cta-reviews"
              >
                Enter the archive
              </MagneticButton>
              <MagneticButton
                href="#manifesto"
                variant="ghost"
                testid="home-hero-cta-manifesto"
                onClick={(e) => {
                  e.preventDefault();
                  document
                    .getElementById("manifesto")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                Why COLAB
              </MagneticButton>
            </div>
          </motion.div>
        </div>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 0.6 }}
          className="flex items-end justify-between text-[10px] uppercase tracking-[0.3em] font-mono text-white/50"
        >
          <div className="flex items-center gap-3">
            <span className="w-8 h-px bg-white/40" />
            <span>Scroll to enter</span>
          </div>
          <div className="hidden md:flex items-center gap-6">
            <span>65+ Immersive Theatre Reviews</span>
            <span className="w-1 h-1 bg-white/40 rounded-full" />
            <span>15 Immersive Productions</span>
            <span className="w-1 h-1 bg-white/40 rounded-full" />
            <span>Best Immersive Theatre London</span>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}
